import React, { useEffect, useMemo, useState } from 'react';
import { DragIcon } from '@/components/Froala/widgets/ChatGPT/Icon/index';
import { useMobile } from '@/util';
import { IconButton } from '@teo/components';
import { CrossFilled } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';

const shuffleArray = (arr) => {
  const copy = [...arr];

  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
};

const buildInitialState = (value, answer, answered, shuffleItems) => {
  const sourceGroups = Array.isArray(value) ? value : [];
  const answerGroups = Array.isArray(answer) ? answer : [];

  const allItems = sourceGroups.flatMap((group) =>
    (group.items || []).map((item) => ({
      uid: item.uid,
      label: item.label,
    }))
  );

  if (answered) {
    const parentMap = {};
    sourceGroups.forEach((group) => {
      (group.items || []).forEach((item) => {
        parentMap[item.uid] = group.uid;
      });
    });

    const groupsFromAnswer = sourceGroups.map((group) => {
      const answeredGroup = answerGroups.find((a) => a.uid === group.uid);

      return {
        uid: group.uid,
        title: group.title,
        items: (answeredGroup?.items || []).map((item) => ({
          uid: item.uid,
          label: item.label,
          parentUid: parentMap[item.uid],
        })),
      };
    });

    const studentPlacementMap = {};
    answerGroups.forEach((group) => {
      (group.items || []).forEach((item) => {
        studentPlacementMap[item.uid] = group.uid;
      });
    });

    const resultGroups = sourceGroups.map((group) => ({
      uid: group.uid,
      title: group.title,
      items: (group.items || []).map((item) => ({
        uid: item.uid,
        label: item.label,
        parentUid: group.uid,
        isCorrect: studentPlacementMap[item.uid] === group.uid,
      })),
    }));

    const usedItemIds = new Set(
      groupsFromAnswer.flatMap((group) => group.items.map((item) => item.uid))
    );

    const bankItems = allItems.filter((item) => !usedItemIds.has(item.uid));

    return {
      groups: groupsFromAnswer,
      bankItems,
      resultGroups,
    };
  }

  const groups = sourceGroups.map((group) => ({
    uid: group.uid,
    title: group.title,
    items: [],
  }));

  const bankItems = shuffleItems ? shuffleArray(allItems) : allItems;

  return {
    groups,
    bankItems,
  };
};

export default function GroupedItemsLearnerWidget({
  state,
  value = [],
  answer,
  answered,
  onChange,
  shuffleItems = true,
}) {
  const isMobile = useMobile();
  const { t } = useTranslation();

  const initialState = useMemo(
    () => buildInitialState(value, answer, answered, shuffleItems),
    [value, answer, answered, shuffleItems]
  );

  const [groups, setGroups] = useState(initialState.groups);
  const [bankItems, setBankItems] = useState(initialState.bankItems);
  const [resultGroups, setResultGroups] = useState(initialState?.resultGroups);

  const [draggingItem, setDraggingItem] = useState(null);
  const [dragOverZone, setDragOverZone] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);

  const [isMouseDown, setIsMouseDown] = useState(false);

  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const [mobileSelectedItem, setMobileSelectedItem] = useState(null);

  const showCorrect =
    state.excludeScore !== undefined ? !state.excludeScore : true;

  useEffect(() => {
    if (answered) {
      setGroups(initialState.groups);
      setBankItems(initialState.bankItems);
      setResultGroups(initialState?.resultGroups);
    }

    setDraggingItem(null);
    setDragOverZone(null);
    setDragPreview(null);
    setMobileSheetOpen(false);
    setMobileSelectedItem(null);
  }, [answered, value, answer, shuffleItems]);

  useEffect(() => {
    if (answered) {
      if (isMouseDown) {
        setGroups(initialState.resultGroups);
      } else {
        setGroups(initialState.groups);
      }
    }
  }, [isMouseDown, answered, initialState.groups, initialState.resultGroups]);

  const emitChange = (nextGroups, finalBankItems) => {
    if (onChange && !answered) {
      onChange(nextGroups, finalBankItems);
    }
  };

  const findItemById = (
    itemId,
    currentGroups = groups,
    currentBankItems = bankItems
  ) => {
    const bankItem = currentBankItems.find((item) => item.uid === itemId);
    if (bankItem) return bankItem;

    for (const group of currentGroups) {
      const found = group.items.find((item) => item.uid === itemId);
      if (found) return found;
    }

    return null;
  };

  const findItemLocation = (
    itemId,
    currentGroups = groups,
    currentBankItems = bankItems
  ) => {
    const inBank = currentBankItems.find((item) => item.uid === itemId);
    if (inBank) {
      return { type: 'bank', groupUid: null };
    }

    for (const group of currentGroups) {
      const found = group.items.find((item) => item.uid === itemId);
      if (found) {
        return { type: 'group', groupUid: group.uid };
      }
    }

    return null;
  };

  const removeItemFromEverywhere = (
    itemId,
    currentGroups = groups,
    currentBankItems = bankItems
  ) => {
    const nextBankItems = currentBankItems.filter(
      (item) => item.uid !== itemId
    );

    const nextGroups = currentGroups.map((group) => ({
      ...group,
      items: group.items.filter((item) => item.uid !== itemId),
    }));

    return {
      nextGroups,
      nextBankItems,
    };
  };

  const moveToGroup = (itemId, targetGroupId) => {
    if (answered) return;

    const item = findItemById(itemId);
    if (!item) return;

    const { nextGroups, nextBankItems } = removeItemFromEverywhere(itemId);

    const finalGroups = nextGroups.map((group) =>
      group.uid === targetGroupId
        ? {
            ...group,
            items: [...group.items, item],
          }
        : group
    );

    setGroups(finalGroups);
    setBankItems(nextBankItems);
    emitChange(finalGroups, nextBankItems);
  };

  const moveToBank = (itemId) => {
    if (answered) return;

    const item = findItemById(itemId);
    if (!item) return;

    const { nextGroups, nextBankItems } = removeItemFromEverywhere(itemId);

    const finalBankItems = [...nextBankItems, item];

    setGroups(nextGroups);
    setBankItems(finalBankItems);
    emitChange(nextGroups, finalBankItems);
  };

  const handleMouseUpGlobal = () => {
    if (answered || isMobile) return;

    if (draggingItem && dragOverZone) {
      if (dragOverZone.type === 'bank') {
        moveToBank(draggingItem.itemId);
      }

      if (dragOverZone.type === 'group') {
        moveToGroup(draggingItem.itemId, dragOverZone.groupId);
      }
    }

    setDraggingItem(null);
    setDragOverZone(null);
    setDragPreview(null);
  };

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUpGlobal);

    return () => {
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!draggingItem || answered || isMobile) return;

      setDragPreview({
        x: e.clientX,
        y: e.clientY,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [draggingItem, answered, isMobile]);

  const draggedItem = draggingItem ? findItemById(draggingItem.itemId) : null;
  const shouldShowBank = bankItems?.length > 0;

  const hasIncorrectItems = resultGroups?.some((group) =>
    group?.items?.some((item) => item?.isCorrect === false)
  );

  const shouldShowCorrectHint =
    answered && bankItems?.length > 0 && hasIncorrectItems;

  const handleMouseDown = () => {
    answered && showCorrect && setIsMouseDown(true);
  };

  const handleMouseUp = () => {
    answered && showCorrect && setIsMouseDown(false);
  };

  const openMobileSheet = (itemUid) => {
    if (!isMobile || answered) return;

    const item = findItemById(itemUid);
    const location = findItemLocation(itemUid);

    if (!item || !location) return;

    setMobileSelectedItem({
      uid: item.uid,
      label: item.label,
      sourceType: location.type,
      sourceGroupUid: location.groupUid,
    });

    setMobileSheetOpen(true);
  };

  const closeMobileSheet = () => {
    setMobileSheetOpen(false);
    setMobileSelectedItem(null);
  };

  const handleSelectGroupMobile = (groupUid) => {
    if (!mobileSelectedItem) return;
    moveToGroup(mobileSelectedItem.uid, groupUid);
    closeMobileSheet();
  };

  const handleRemoveFromGroupMobile = () => {
    if (!mobileSelectedItem) return;
    moveToBank(mobileSelectedItem.uid);
    closeMobileSheet();
  };

  return (
    <div className="">
      {shouldShowBank && (
        <div
          data-scorm-sort-bank
          onMouseEnter={() => {
            if (draggingItem && !answered && !isMobile) {
              setDragOverZone({ type: 'bank' });
            }
          }}
          className={`mb-6 rounded-[8px] px-2 py-2 transition ${
            dragOverZone?.type === 'bank' && !answered && !isMobile
              ? 'bg-blue-50 ring-2 ring-blue-400'
              : ''
          }`}
        >
          <div
            data-scorm-sort-items
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {bankItems?.map((item) => {
              const isDraggingCurrentItem =
                draggingItem && draggingItem.itemId === item.uid;

              return (
                <div
                  key={item.uid}
                  onClick={() => {
                    if (isMobile && !answered) {
                      openMobileSheet(item.uid);
                    }
                  }}
                  className={`relative flex items-center gap-3 rounded-[4px] bg-grey-02 px-2 py-2 transition ${
                    isDraggingCurrentItem ? 'opacity-50' : ''
                  } ${answered && 'border border-error-04 !bg-error-01 px-3'} ${
                    isMobile && !answered ? 'cursor-pointer' : ''
                  }`}
                >
                  {!answered && !isMobile && (
                    <div
                      onMouseDown={() =>
                        setDraggingItem({
                          itemId: item.uid,
                          source: 'bank',
                        })
                      }
                      className="absolute inset-0 cursor-grab select-none active:cursor-grabbing"
                    />
                  )}

                  {!answered && <DragIcon className="w-[10px] text-grey-04" />}

                  <div className="text-sm text-grey-08">{item.label}</div>
                </div>
              );
            })}
          </div>

          {!answered &&
            !isMobile &&
            draggingItem &&
            dragOverZone?.type === 'bank' && (
              <div className="mt-3 rounded-[4px] border border-dashed border-blue-400 bg-blue-50 px-2 py-2 text-sm text-blue-500">
                {t('widgets.edit.sort_groups.drop_here')}
              </div>
            )}
        </div>
      )}
      {shouldShowCorrectHint && (
        <div className="mt-3 px-3 text-xs font-medium text-error-04">
          {t('widgets.edit.sort_groups.press_item')}
        </div>
      )}
      <div
        className="grid grid-cols-1 gap-4 px-2 sm:grid-cols-2"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
      >
        {groups?.map((group) => {
          const groupId = group.uid;
          const isDragOver =
            !answered &&
            !isMobile &&
            dragOverZone?.type === 'group' &&
            dragOverZone.groupId === group.uid;

          return (
            <div
              key={group.uid}
              data-scorm-sort-group={group.uid}
              onMouseEnter={() => {
                if (draggingItem && !answered && !isMobile) {
                  setDragOverZone({
                    type: 'group',
                    groupId: group.uid,
                  });
                }
              }}
              className={`rounded-lg border border-grey-02 bg-white p-4 transition ${
                isDragOver
                  ? 'border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.15)]'
                  : 'border-[#e3e3e3]'
              }`}
            >
              <div className="mb-4 text-sm font-medium text-black">
                {group.title}
              </div>

              <div data-scorm-sort-items className="space-y-3">
                {group.items.length === 0 && !isDragOver && (
                  <div className="rounded-lg border border-dashed border-[#ececec] px-2 py-2 text-sm text-grey-04">
                    {t('widgets.edit.sort_groups.no_items')}
                  </div>
                )}

                {!answered && !isMobile && isDragOver && (
                  <div className="rounded-[4px] border border-dashed border-blue-400 bg-blue-50 px-2 py-2 text-sm text-blue-500">
                    {t('widgets.edit.sort_groups.drop_here')}
                  </div>
                )}

                {group.items.map((item) => {
                  const isDraggingCurrentItem =
                    draggingItem && draggingItem.itemId === item.uid;

                  return (
                    <div
                      key={item.uid}
                      onClick={() => {
                        if (isMobile && !answered) {
                          openMobileSheet(item.uid);
                        }
                      }}
                      className={`relative flex items-center gap-3 rounded-[4px] bg-grey-02 px-2 py-2 transition ${
                        isDraggingCurrentItem ? 'opacity-50' : ''
                      } ${answered ? 'border px-3' : '!py-1'} ${
                        answered &&
                        (isMouseDown
                          ? item?.isCorrect
                            ? 'border-success-04 !bg-success-01'
                            : 'border-secondary-04 !bg-secondary-01'
                          : groupId === item?.parentUid
                          ? 'border-success-04 !bg-success-01'
                          : 'border-error-04 !bg-error-01')
                      } ${isMobile && !answered ? 'cursor-pointer !py-1' : ''}`}
                    >
                      {!answered && !isMobile && (
                        <div
                          onMouseDown={() =>
                            setDraggingItem({
                              itemId: item.uid,
                              source: 'group',
                              groupId: group.uid,
                            })
                          }
                          className="absolute inset-0 cursor-grab select-none active:cursor-grabbing"
                        />
                      )}

                      {!answered && (
                        <DragIcon className="w-[10px] text-grey-04" />
                      )}

                      <div className="flex-1 text-sm text-grey-08">
                        {item.label}
                      </div>

                      {!answered && (
                        <IconButton
                          className="z-1 relative ml-auto !text-grey-06"
                          Icon={CrossFilled}
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            moveToBank(item.uid);
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!answered && !isMobile && draggingItem && dragPreview && draggedItem && (
        <div
          style={{
            position: 'fixed',
            top: dragPreview.y + 12,
            left: dragPreview.x + 12,
            pointerEvents: 'none',
            zIndex: 9999,
          }}
          className="min-w-[200px] rounded-[4px] border border-[#d9d9d9] bg-white px-3 py-2 text-[14px] text-[#3a3a3a] opacity-95 shadow-lg"
        >
          {draggedItem.label}
        </div>
      )}

      {isMobile && !answered && mobileSheetOpen && (
        <>
          <div
            className="fixed inset-0 z-[9998] bg-black/30"
            onClick={closeMobileSheet}
          />

          <div className="fixed inset-x-0 bottom-0 z-[9999] rounded-t-2xl bg-white px-4 pb-6 pt-3 shadow-[0_-8px_30px_rgba(0,0,0,0.16)]">
            <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-grey-03" />

            <div className="mb-3">
              <div className="text-sm font-medium text-black">
                {t('widgets.edit.sort_groups.sort_into')}
              </div>

              <div className="mt-2 rounded-[4px] bg-grey-02 px-3 py-2 text-sm text-grey-08">
                {mobileSelectedItem?.label}
              </div>
            </div>

            <div className="space-y-2">
              {groups.map((group) => {
                const isCurrentGroup =
                  mobileSelectedItem?.sourceType === 'group' &&
                  mobileSelectedItem?.sourceGroupUid === group.uid;

                return (
                  <button
                    key={group.uid}
                    type="button"
                    onClick={() => handleSelectGroupMobile(group.uid)}
                    className={`block w-full rounded-lg border px-3 py-3 text-left text-sm ${
                      isCurrentGroup
                        ? 'border-secondary-04 bg-secondary-01 text-black'
                        : 'border-grey-02 bg-white text-black'
                    }`}
                  >
                    {group.title}
                  </button>
                );
              })}

              {mobileSelectedItem?.sourceType === 'group' && (
                <button
                  type="button"
                  onClick={handleRemoveFromGroupMobile}
                  className="block w-full rounded-lg border border-error-03 bg-error-01 px-3 py-3 text-left text-sm text-error-06"
                >
                  {t('widgets.edit.sort_groups.remove_from_group')}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
